"use client";

import gsap from "gsap";
import Image from "next/image";
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

export type PortalImage = {
  id: string;
  src: string;
  alt: string;
};

type PortalState = {
  active: boolean;
  centerX: number;
  centerY: number;
  edgeAlpha: number;
  intensity: number;
  radius: number;
  time: number;
};

type Size = {
  width: number;
  height: number;
};

type OrganicPortalSliderProps = {
  images: PortalImage[];
  className?: string;
  waitMs?: number;
  fit?: "contain" | "cover";
  onActiveIndexChange?: (index: number) => void;
};

const createPortalState = (): PortalState => ({
  active: false,
  centerX: 0,
  centerY: 0,
  edgeAlpha: 0,
  intensity: 0,
  radius: 0,
  time: 0,
});

function getOrganicWave(angle: number, time: number) {
  return (
    Math.sin(angle * 3 + time * 1.7) * 0.085 +
    Math.sin(angle * 7 - time * 2.3) * 0.045 +
    Math.sin(angle * 13 + time * 3.1) * 0.025 +
    Math.sin(angle * 19 - time * 1.1) * 0.012
  );
}

function getOrganicPath(
  centerX: number,
  centerY: number,
  radius: number,
  time: number
) {
  const points = Array.from({ length: 96 }, (_, index) => {
    const angle = (index / 96) * Math.PI * 2;
    const wave = getOrganicWave(angle, time);
    const localRadius = Math.max(0, radius * (1 + wave));

    return {
      x: centerX + Math.cos(angle) * localRadius,
      y: centerY + Math.sin(angle) * localRadius,
    };
  });

  const midpoint = (a: (typeof points)[number], b: (typeof points)[number]) => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  });
  const first = midpoint(points[points.length - 1], points[0]);
  let path = `M ${first.x.toFixed(2)} ${first.y.toFixed(2)}`;

  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length];
    const middle = midpoint(point, next);
    path += ` Q ${point.x.toFixed(2)} ${point.y.toFixed(
      2
    )} ${middle.x.toFixed(2)} ${middle.y.toFixed(2)}`;
  });

  return `${path} Z`;
}

function ImageStack({
  image,
  fit,
}: {
  image: PortalImage;
  fit: "contain" | "cover";
}) {
  return (
    <Image
      key={image.id}
      className="organic-portal__base-image"
      src={image.src}
      alt={image.alt}
      fill
      quality={90}
      sizes="(max-width: 991px) 100vw, 520px"
      style={{ objectFit: fit }}
    />
  );
}

function PortalReveal({
  image,
  fit,
  running,
  size,
  stateRef,
}: {
  image: PortalImage;
  fit: "contain" | "cover";
  running: boolean;
  size: Size;
  stateRef: MutableRefObject<PortalState>;
}) {
  const rawId = useId().replace(/:/g, "");
  const maskId = `portal-mask-${rawId}`;
  const distortionId = `portal-distortion-${rawId}`;
  const maskPathRef = useRef<SVGPathElement>(null);
  const turbulenceRef = useRef<SVGFETurbulenceElement>(null);
  const displacementRef = useRef<SVGFEDisplacementMapElement>(null);

  useEffect(() => {
    // Collapsing while idle prevents the previous run's fully-open mask from
    // flashing the next image before a new portal starts.
    if (!running) {
      maskPathRef.current?.setAttribute("d", "M 0 0 Z");
      return;
    }

    let frame = 0;

    const render = () => {
      const portal = stateRef.current;

      if (portal.active && maskPathRef.current) {
        const path = getOrganicPath(
          portal.centerX,
          portal.centerY,
          portal.radius,
          portal.time
        );
        maskPathRef.current.setAttribute("d", path);
        turbulenceRef.current?.setAttribute(
          "baseFrequency",
          `${0.009 + Math.sin(portal.time * 0.8) * 0.0015} ${
            0.016 + Math.cos(portal.time * 0.65) * 0.002
          }`
        );
        displacementRef.current?.setAttribute(
          "scale",
          String(22 + portal.intensity * 18)
        );
      }

      frame = window.requestAnimationFrame(render);
    };

    frame = window.requestAnimationFrame(render);
    return () => window.cancelAnimationFrame(frame);
  }, [running, stateRef]);

  return (
    <svg
      className="organic-portal__reveal"
      viewBox={`0 0 ${size.width} ${size.height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <filter
          id={distortionId}
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            ref={turbulenceRef}
            type="fractalNoise"
            baseFrequency="0.009 0.016"
            numOctaves="3"
            seed="8"
            result="noise"
          />
          <feDisplacementMap
            ref={displacementRef}
            in="SourceGraphic"
            in2="noise"
            scale="34"
            xChannelSelector="R"
            yChannelSelector="B"
            result="displaced"
          />
          <feGaussianBlur in="displaced" stdDeviation="1.35" />
        </filter>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={size.width}
          height={size.height}
        >
          <rect width={size.width} height={size.height} fill="black" />
          <path
            ref={maskPathRef}
            fill="white"
            filter={`url(#${distortionId})`}
          />
        </mask>
      </defs>

      <image
        href={image.src}
        width={size.width}
        height={size.height}
        preserveAspectRatio={
          fit === "cover" ? "xMidYMid slice" : "xMidYMid meet"
        }
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}

type ParticleSeed = {
  angle: number;
  drift: number;
  radialOffset: number;
  size: number;
  phase: number;
  brightness: number;
  fog: boolean;
  star: boolean;
  warm: boolean;
};

type ParticleSprites = {
  fog: HTMLCanvasElement;
  silver: HTMLCanvasElement;
  warm: HTMLCanvasElement;
  white: HTMLCanvasElement;
};

// Pre-rendered bloom sprites keep the edge cheap to draw: per-particle
// shadowBlur or canvas filters cannot hold 60fps at this particle count.
function createGlowSprite(rgb: string, coreStop: number, coreAlpha: number) {
  const dimension = 64;
  const canvas = document.createElement("canvas");
  canvas.width = dimension;
  canvas.height = dimension;

  const context = canvas.getContext("2d");
  if (!context) return canvas;

  const center = dimension / 2;
  const gradient = context.createRadialGradient(
    center,
    center,
    0,
    center,
    center,
    center
  );
  gradient.addColorStop(0, `rgba(${rgb},${coreAlpha})`);
  gradient.addColorStop(coreStop, `rgba(${rgb},${coreAlpha * 0.42})`);
  gradient.addColorStop(0.55, `rgba(${rgb},${coreAlpha * 0.1})`);
  gradient.addColorStop(1, `rgba(${rgb},0)`);
  context.fillStyle = gradient;
  context.fillRect(0, 0, dimension, dimension);

  return canvas;
}

function createParticleSprites(): ParticleSprites {
  return {
    fog: createGlowSprite("235,239,242", 0.5, 0.5),
    silver: createGlowSprite("207,214,222", 0.15, 0.9),
    warm: createGlowSprite("255,238,213", 0.15, 0.95),
    white: createGlowSprite("255,255,255", 0.13, 1),
  };
}

function createParticles(): ParticleSeed[] {
  const count = 560;

  return Array.from({ length: count }, (_, index) => ({
    angle: (index / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.035,
    drift: (Math.random() - 0.5) * 0.055,
    radialOffset: (Math.random() - 0.5) * 25,
    size: 0.25 + Math.pow(Math.random(), 2.2) * 1.8,
    phase: Math.random() * Math.PI * 2,
    brightness: 0.18 + Math.random() * 0.82,
    fog: index % 9 === 0,
    star: index % 47 === 0,
    warm: index % 79 === 0,
  }));
}

function ParticleLayer({
  running,
  size,
  stateRef,
}: {
  running: boolean;
  size: Size;
  stateRef: MutableRefObject<PortalState>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<ParticleSeed[] | null>(null);
  const spritesRef = useRef<ParticleSprites | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size.width * ratio);
    canvas.height = Math.round(size.height * ratio);
    const context = canvas.getContext("2d");
    if (!context) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, size.width, size.height);

    if (!running) return;

    if (!particlesRef.current) {
      particlesRef.current = createParticles();
    }
    if (!spritesRef.current) {
      spritesRef.current = createParticleSprites();
    }
    const particles = particlesRef.current;
    const sprites = spritesRef.current;

    let frame = 0;
    const render = () => {
      const portal = stateRef.current;
      context.clearRect(0, 0, size.width, size.height);

      if (portal.active && portal.edgeAlpha > 0.01) {
        context.save();
        context.globalCompositeOperation = "lighter";

        const getParticlePosition = (particle: ParticleSeed) => {
          const angle = particle.angle + portal.time * particle.drift;
          const edgeRadius =
            portal.radius * (1 + getOrganicWave(angle, portal.time));
          const turbulence =
            Math.sin(angle * 17 - portal.time * 3.2 + particle.phase) * 4 +
            Math.sin(angle * 31 + portal.time * 1.8) * 2.5;
          const distance =
            edgeRadius +
            particle.radialOffset * (0.55 + portal.intensity * 0.45) +
            turbulence;

          return {
            angle,
            x: portal.centerX + Math.cos(angle) * distance,
            y: portal.centerY + Math.sin(angle) * distance,
          };
        };

        const drawSprite = (
          sprite: HTMLCanvasElement,
          x: number,
          y: number,
          drawSize: number,
          alpha: number
        ) => {
          if (alpha <= 0.004) return;
          context.globalAlpha = Math.min(1, alpha);
          context.drawImage(
            sprite,
            x - drawSize / 2,
            y - drawSize / 2,
            drawSize,
            drawSize
          );
        };

        // Broad, broken-up dust clouds provide bloom without forming a line.
        particles.forEach((particle) => {
          if (!particle.fog) return;
          const density =
            Math.sin(particle.angle * 5 + portal.time * 0.9) +
            Math.sin(particle.angle * 11 - portal.time * 1.4) * 0.65;
          if (density < -0.45) return;

          const { x, y } = getParticlePosition(particle);
          drawSprite(
            sprites.fog,
            x,
            y,
            26 + particle.size * 20 + portal.intensity * 14,
            portal.edgeAlpha * particle.brightness * 0.1 * (density + 0.6)
          );
        });

        particles.forEach((particle) => {
          const density =
            Math.sin(particle.angle * 5 + portal.time * 0.9) +
            Math.sin(particle.angle * 11 - portal.time * 1.55) * 0.62 +
            Math.sin(particle.angle * 23 + portal.time * 2.1 + particle.phase) *
              0.28;

          // Moving thresholds create natural gaps that never settle in place.
          if (density < -0.72 + Math.sin(portal.time + particle.phase) * 0.12) {
            return;
          }

          const { x, y } = getParticlePosition(particle);
          const twinkle =
            0.32 +
            (Math.sin(portal.time * 4.5 + particle.phase) + 1) * 0.34;
          const alpha = Math.min(
            0.95,
            portal.edgeAlpha *
              particle.brightness *
              twinkle *
              Math.max(0.12, (density + 1.1) / 2)
          );
          const particleSize =
            particle.size * (0.72 + portal.intensity * 0.4);
          const sprite = particle.warm
            ? sprites.warm
            : particle.brightness > 0.68
            ? sprites.white
            : sprites.silver;

          drawSprite(sprite, x, y, particleSize * 9, alpha);

          if (particle.star && density > 0.15) {
            const ray = 2.4 + particle.size * 3.6;
            context.globalAlpha = Math.min(1, alpha * 0.85);
            context.fillStyle = "#ffffff";
            context.fillRect(x - ray, y - 0.35, ray * 2, 0.7);
            context.fillRect(x - 0.35, y - ray, 0.7, ray * 2);
          }
        });

        context.globalAlpha = 1;

        context.restore();
      }

      frame = window.requestAnimationFrame(render);
    };

    frame = window.requestAnimationFrame(render);
    return () => window.cancelAnimationFrame(frame);
  }, [running, size, stateRef]);

  return <canvas ref={canvasRef} className="organic-portal__particles" />;
}

export default function OrganicPortalSlider({
  images,
  className = "",
  waitMs = 4500,
  fit = "cover",
  onActiveIndexChange,
}: OrganicPortalSliderProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<PortalState>(createPortalState());
  const transitionRef = useRef(false);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [incomingIndex, setIncomingIndex] = useState(
    images.length > 1 ? 1 : 0
  );
  const [transitioning, setTransitioning] = useState(false);
  const [size, setSize] = useState<Size>({ width: 520, height: 460 });

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const updateSize = () => {
      const bounds = frame.getBoundingClientRect();
      setSize({
        width: Math.max(1, bounds.width),
        height: Math.max(1, bounds.height),
      });
    };
    const observer = new ResizeObserver(updateSize);
    updateSize();
    observer.observe(frame);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    images.forEach(({ src }) => {
      const image = new window.Image();
      image.src = src;
    });
  }, [images]);

  const startTransition = useCallback(() => {
    if (images.length < 2 || transitionRef.current) return;

    const frame = frameRef.current;
    if (!frame) return;

    const nextIndex = (activeIndex + 1) % images.length;
    const bounds = frame.getBoundingClientRect();
    const centerX = 0;
    const centerY = bounds.height;
    const farthestX = Math.max(centerX, bounds.width - centerX);
    const farthestY = Math.max(centerY, bounds.height - centerY);
    const coverRadius = Math.hypot(farthestX, farthestY) * 1.42;
    const portal = stateRef.current;

    transitionRef.current = true;
    setIncomingIndex(nextIndex);
    setTransitioning(true);
    Object.assign(portal, {
      active: true,
      centerX,
      centerY,
      edgeAlpha: 0,
      intensity: 0,
      radius: 2,
      time: 0,
    });

    const expandDuration = 2.2;
    const startedAt = performance.now();
    // Random phase keeps each transition's silhouette unique without
    // discontinuities inside a single run.
    const timePhase = Math.random() * 40;
    portal.time = timePhase;

    timelineRef.current?.kill();
    timelineRef.current = gsap
      .timeline({
        onUpdate: () => {
          portal.time = timePhase + (performance.now() - startedAt) / 1000;
        },
        onComplete: () => {
          portal.active = false;
          transitionRef.current = false;
          setTransitioning(false);
        },
      })
      .to(
        portal,
        {
          radius: coverRadius,
          duration: expandDuration,
          ease: "power1.inOut",
          onComplete: () => {
            setActiveIndex(nextIndex);
            onActiveIndexChange?.(nextIndex);
          },
        },
        0
      )
      .to(
        portal,
        {
          edgeAlpha: 1,
          intensity: 1,
          duration: 0.3,
          ease: "sine.out",
        },
        0
      )
      .to(
        portal,
        {
          edgeAlpha: 0,
          intensity: 0,
          duration: 0.5,
          ease: "sine.inOut",
        },
        expandDuration - 0.3
      );
  }, [activeIndex, images.length, onActiveIndexChange]);

  useEffect(() => {
    if (images.length < 2 || transitioning) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const timeout = window.setTimeout(() => {
        const nextIndex = (activeIndex + 1) % images.length;
        setActiveIndex(nextIndex);
        onActiveIndexChange?.(nextIndex);
      }, waitMs);
      return () => window.clearTimeout(timeout);
    }

    const timeout = window.setTimeout(startTransition, waitMs);
    return () => window.clearTimeout(timeout);
  }, [
    activeIndex,
    images.length,
    onActiveIndexChange,
    startTransition,
    transitioning,
    waitMs,
  ]);

  useEffect(
    () => () => {
      timelineRef.current?.kill();
    },
    []
  );

  if (!images.length) return null;

  const active = images[activeIndex] ?? images[0];
  const incoming = images[incomingIndex] ?? images[0];

  return (
    <div ref={frameRef} className={`organic-portal ${className}`.trim()}>
      <ImageStack image={active} fit={fit} />
      <PortalReveal
        image={incoming}
        fit={fit}
        running={transitioning}
        size={size}
        stateRef={stateRef}
      />
      <ParticleLayer
        running={transitioning}
        size={size}
        stateRef={stateRef}
      />

      <style jsx>{`
        .organic-portal {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          isolation: isolate;
          background: transparent;
        }

        .organic-portal :global(.organic-portal__base-image),
        .organic-portal :global(.organic-portal__reveal),
        .organic-portal :global(.organic-portal__particles) {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        .organic-portal :global(.organic-portal__base-image) {
          z-index: 0;
        }

        .organic-portal :global(.organic-portal__reveal) {
          z-index: 1;
          overflow: visible;
          pointer-events: none;
        }

        .organic-portal :global(.organic-portal__particles) {
          z-index: 2;
          pointer-events: none;
          mix-blend-mode: screen;
        }
      `}</style>
    </div>
  );
}
